import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { EaseKey, getEase, Tween } from "@westpierstudio/luma/createjs";
import { Emitter } from "pixi-particles";
import { JackpotMeters } from "./JackpotMeters";
import { Orientation } from "@westpierstudio/luma/layout";
import { Container, Sprite } from "pixi.js";

enum State {
    INIT,
    IDLE,
    FORCED_LOGO
}

export class ReelTopDisplay extends View {
    private readonly transitionWait: number;
    private readonly transitionTime: number;
    private readonly logoContainer: Container;
    private readonly jackpotContainer: Container;
    private readonly logo: Sprite;
    private readonly logoEmitter: Emitter;
    private readonly freeSpinsLogo: Container;
    private readonly freeSpinsLogoTop: Sprite;
    private readonly freeSpinsLogoBottom: Sprite;
    private readonly freeSpinsLogoEmitterTop: Emitter;
    private readonly freeSpinsLogoEmitterBottom: Emitter;
    private readonly jackpotMeters: JackpotMeters;
    private easeIn: EaseKey;
    private easeOut: EaseKey;

    constructor(protected override readonly services: GameServices) {
        super(services);

        const {factory, resources} = this.services;

        const settings = this.services.resources.getSetting(`reelTopDisplay`);

        this.transitionWait = settings.transitionWait;
        this.transitionTime = settings.transitionTime;
        this.easeIn = settings.tweenIn;
        this.easeOut = settings.tweenOut;

        const spec = resources.getSpec(`reeltopdisplay`);
        factory.applySpec(this, spec);

        this.logoContainer = factory.createContainer({}, this);
        this.jackpotContainer = factory.createContainer({visible: false}, this);

        this.logo = factory.createSprite(spec.reelTopDisplayLogo, this.logoContainer);
        this.freeSpinsLogo = factory.createContainer({name: `freeSpinsLogo`});
        this.freeSpinsLogoTop = factory.createSprite(spec.reelTopDisplayFreeSpinsLogoTop, this.freeSpinsLogo);
        this.freeSpinsLogoBottom = factory.createSprite(spec.reelTopDisplayFreeSpinsLogoBottom, this.freeSpinsLogo);

        const emitterSpecs = resources.getSpec(`particles`);
        this.logoEmitter = factory.createParticleEmitter(emitterSpecs.logo, this.logoContainer);
        this.freeSpinsLogoEmitterTop = factory.createParticleEmitter(emitterSpecs.freeSpinsLogoTop, this.logoContainer);
        this.freeSpinsLogoEmitterBottom = factory.createParticleEmitter(emitterSpecs.freeSpinsLogoBottom, this.logoContainer);
        this.logoEmitter.emit = true;

        const jackpotMetersSpec = resources.getSpec(`reeltopdisplayjackpotmeters`);
        const scale = jackpotMetersSpec.reelTopDisplayScale;

        this.jackpotMeters = this.jackpotContainer.addChild(new JackpotMeters(this.services, jackpotMetersSpec));
        this.jackpotMeters.scale.set(scale, scale);

        this.setLogo();
    }

    resize(orientation: Orientation) {
        const {top} = this.services.layout.getScreenBoundaryInBucketSpace();
        const isSpace = top < this.freeSpinsLogoTop.y;
        const showTop = isSpace;
        this.freeSpinsLogoTop.visible = showTop;
        this.freeSpinsLogoEmitterTop.parent.visible = showTop;
    }

    setJackpotvalues(major: number, minor: number, mini: number): void {
        this.jackpotMeters.setJackpotvalues(major, minor, mini);
    }

    setForcedLogo(): void {
        this.setLogo();
    }

    baseGameMode(): void {
        this.logoEmitter.emit = true;
        this.freeSpinsLogoEmitterTop.emit = false;
        this.freeSpinsLogoEmitterBottom.emit = false;
        this.logoContainer.removeChildren();
        this.logoContainer.addChild(this.logo);
        this.logoContainer.addChild(this.logoEmitter.parent);
    }

    freeSpinsMode(): void {
        this.logoEmitter.emit = false;
        this.freeSpinsLogoEmitterTop.emit = true;
        this.freeSpinsLogoEmitterBottom.emit = true;
        this.logoContainer.removeChildren();
        this.logoContainer.addChild(this.freeSpinsLogo);
        this.logoContainer.addChild(this.freeSpinsLogoEmitterTop.parent);
        this.logoContainer.addChild(this.freeSpinsLogoEmitterBottom.parent);
    }

     /**
     *
     * @param dt Seconds
    */
    tick(dt: number): void {

        // if (this.state === State.IDLE) {

        //     const ms = dt * 1000;

        //     this.transitionWaitTimer += ms;

        //     if (this.transitionWaitTimer >= this.transitionWait) {

        //         this.transitionWaitTimer = 0;

        //         if (this.logoContainer.visible) {
        //             this.transitionJackpots();
        //         } else {
        //             this.transitionLogo();
        //         }
        //     }
        // }

        this.jackpotMeters.tick();
    }

    override reset(): void {

        this.logoEmitter.emit = false;
        this.freeSpinsLogoEmitterTop.emit = false;
        this.freeSpinsLogoEmitterBottom.emit = false;
        this.baseGameMode();
        this.setLogo();
    }

    private setLogo(): void {
        this.clearTransition();
        this.logoContainer.visible = true;
        this.jackpotContainer.visible = false;
    }

    transitionJackpots(): void {

        Tween.get(this.logoContainer.scale)
        .to({x: 0, y: 0}, this.transitionTime/2, getEase(this.easeOut))
        .call(() => {

            this.logoContainer.visible = false;

            this.jackpotContainer.scale.set(0,0);
            this.jackpotContainer.visible = true;

            Tween.get(this.jackpotContainer.scale)
            .to({x: 1, y: 1}, this.transitionTime/2, getEase(this.easeIn));
        });
    }

    transitionLogo(): void {

        Tween.get(this.jackpotContainer.scale)
        .to({x: 0, y: 0}, this.transitionTime/2, getEase(this.easeOut))
        .call(() => {

            this.jackpotContainer.visible = false;

            this.logoContainer.scale.set(0,0);
            this.logoContainer.visible = true;

            Tween.get(this.logoContainer.scale)
            .to({x: 1, y: 1}, this.transitionTime/2, getEase(this.easeIn));
        });
    }

    private clearTransition(): void {

        Tween.removeTweens(this.jackpotContainer.scale);
        Tween.removeTweens(this.logoContainer.scale);

        this.jackpotContainer.scale.set(1.0, 1.0);
        this.logoContainer.scale.set(1.0, 1.0);
    }
}